/**
 * PaymentMethodsPage
 *
 * Displays saved billing info and cards from Stripe. All payment data
 * is stored on Stripe — we don't persist billing or card data locally.
 *
 * Layout:
 *   - Billing Information section (read-only display + "Edit" button → dialog)
 *   - Saved Cards section (card list + "Add New Card" button → dialog)
 */

import { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Button, Card, CardContent, Chip, CircularProgress,
    Alert, Dialog, DialogTitle, DialogContent, DialogActions,
    IconButton, Grid, TextField, Select, MenuItem, FormControl, InputLabel,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import StarIcon from '@mui/icons-material/Star';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import {
    CardNumberElement, CardExpiryElement, CardCvcElement,
    useStripe, useElements,
} from '@stripe/react-stripe-js';
import { PaymentService } from '../services/payment-service';
import { useNotification } from '../contexts/NotificationContext';
import type { PaymentMethod, BillingInfo } from '../types/payment';

const paymentService = new PaymentService();

const ELEMENT_STYLE = {
    base: { fontSize: '16px', color: '#e0e0e0', fontFamily: '"Inter","Roboto","Helvetica","Arial",sans-serif', '::placeholder': { color: '#666' } },
    invalid: { color: '#f44336' },
};
const ELEMENT_OPTIONS = { style: ELEMENT_STYLE };

const sectionCard = {
    background: 'linear-gradient(135deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.06) 100%)',
    border: '1px solid rgba(255,255,255,0.08)', borderRadius: 3,
};
const stripeFieldBox = {
    border: '1px solid rgba(255,255,255,0.23)', borderRadius: 1, px: 1.5, py: 1.5,
    '&:hover': { borderColor: 'rgba(255,255,255,0.4)' },
    '&:focus-within': { borderColor: 'primary.main', boxShadow: '0 0 0 2px rgba(33,150,243,0.25)' },
    transition: 'border-color 0.2s, box-shadow 0.2s',
};

function formatBrand(b: string) {
    const m: Record<string, string> = { visa: 'Visa', mastercard: 'Mastercard', amex: 'Amex', discover: 'Discover' };
    return m[b.toLowerCase()] || b.charAt(0).toUpperCase() + b.slice(1);
}

const COUNTRIES = [
    { code: 'US', name: 'United States' }, { code: 'CA', name: 'Canada' },
    { code: 'GB', name: 'United Kingdom' }, { code: 'AU', name: 'Australia' },
    { code: 'DE', name: 'Germany' }, { code: 'FR', name: 'France' },
    { code: 'JP', name: 'Japan' }, { code: 'CN', name: 'China' },
    { code: 'IN', name: 'India' }, { code: 'BR', name: 'Brazil' },
    { code: 'MX', name: 'Mexico' }, { code: 'KR', name: 'South Korea' },
    { code: 'IT', name: 'Italy' }, { code: 'ES', name: 'Spain' },
    { code: 'NL', name: 'Netherlands' }, { code: 'SE', name: 'Sweden' },
    { code: 'NO', name: 'Norway' }, { code: 'DK', name: 'Denmark' },
    { code: 'FI', name: 'Finland' }, { code: 'IE', name: 'Ireland' },
    { code: 'NZ', name: 'New Zealand' }, { code: 'SG', name: 'Singapore' },
    { code: 'HK', name: 'Hong Kong' }, { code: 'TW', name: 'Taiwan' },
    { code: 'CH', name: 'Switzerland' }, { code: 'AT', name: 'Austria' },
    { code: 'BE', name: 'Belgium' }, { code: 'PT', name: 'Portugal' },
    { code: 'PL', name: 'Poland' }, { code: 'CZ', name: 'Czech Republic' },
].sort((a, b) => a.name.localeCompare(b.name));

export function PaymentMethodsPage({ hideBilling = false, vertical = false }: { hideBilling?: boolean; vertical?: boolean } = {}): JSX.Element {
    const stripe = useStripe();
    const elements = useElements();

    const [billing, setBilling] = useState<BillingInfo | null>(null);
    const [methods, setMethods] = useState<PaymentMethod[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Dialogs
    const [billingDialogOpen, setBillingDialogOpen] = useState(false);
    const [cardDialogOpen, setCardDialogOpen] = useState(false);
    const [removeTarget, setRemoveTarget] = useState<PaymentMethod | null>(null);

    // Billing form (dialog)
    const [bf, setBf] = useState({ name: '', email: '', phone: '', line1: '', city: '', state: '', zip: '', country: 'US' });
    const [isSavingBilling, setIsSavingBilling] = useState(false);
    const [billingErrors, setBillingErrors] = useState<Record<string, string>>({});

    // Card form (dialog)
    const [cardholderName, setCardholderName] = useState('');
    const [isAddingCard, setIsAddingCard] = useState(false);
    const [cardFieldsComplete, setCardFieldsComplete] = useState({ number: false, expiry: false, cvc: false });
    const [cardNameError, setCardNameError] = useState('');

    // Other
    const [isRemoving, setIsRemoving] = useState(false);
    const [settingDefaultId, setSettingDefaultId] = useState<string | null>(null);
    const { notify } = useNotification();

    /* ---- Load data ---- */
    const loadData = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const [billingData, methodsData] = await Promise.all([
                paymentService.getBillingInfo().catch(() => null),
                paymentService.listPaymentMethods(),
            ]);
            setBilling(billingData);
            setMethods(methodsData);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load payment data');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => { loadData(); }, [loadData]);

    /* ---- Open billing dialog (pre-fill from current data) ---- */
    const openBillingDialog = () => {
        setBf({
            name: billing?.name || '', email: billing?.email || '', phone: billing?.phone || '',
            line1: billing?.address_line1 || '', city: billing?.address_city || '',
            state: billing?.address_state || '', zip: billing?.address_postal_code || '',
            country: billing?.address_country || 'US',
        });
        setBillingDialogOpen(true);
    };

    /* ---- Save billing to Stripe ---- */
    const handleSaveBilling = async () => {
        // Validate required fields
        const errors: Record<string, string> = {};
        if (!bf.name.trim()) errors.name = 'Name is required';
        if (!bf.email.trim()) errors.email = 'Email is required';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(bf.email)) errors.email = 'Invalid email format';
        if (!bf.country) errors.country = 'Country is required';
        if (Object.keys(errors).length > 0) { setBillingErrors(errors); return; }
        setBillingErrors({});

        setIsSavingBilling(true);
        try {
            await paymentService.createCustomer();
            const updated = await paymentService.updateBillingInfo({
                name: bf.name, email: bf.email, phone: bf.phone,
                address_line1: bf.line1, address_city: bf.city, address_state: bf.state,
                address_postal_code: bf.zip, address_country: bf.country,
            });
            setBilling(updated);
            setBillingDialogOpen(false);
            notify({ message: 'Billing information saved', severity: 'success', autoCloseDuration: 3000 });
        } catch (err) {
            notify({ message: err instanceof Error ? err.message : 'Failed to save billing info', severity: 'error' });
        } finally {
            setIsSavingBilling(false);
        }
    };

    /* ---- Add card via Stripe ---- */
    const handleAddCard = async () => {
        if (!stripe || !elements) return;
        const cardNum = elements.getElement(CardNumberElement);
        if (!cardNum) return;

        // Validate cardholder name
        if (!cardholderName.trim()) {
            setCardNameError('Cardholder name is required');
            return;
        }
        setCardNameError('');

        // Check Stripe field completeness
        if (!cardFieldsComplete.number || !cardFieldsComplete.expiry || !cardFieldsComplete.cvc) {
            notify({ message: 'Please complete all card fields', severity: 'error' });
            return;
        }

        setIsAddingCard(true);
        try {
            await paymentService.createCustomer();
            const { error: sErr, paymentMethod } = await stripe.createPaymentMethod({
                type: 'card', card: cardNum,
                billing_details: {
                    name: cardholderName || billing?.name || undefined,
                    email: billing?.email || undefined,
                    phone: billing?.phone || undefined,
                    address: { line1: billing?.address_line1 || undefined, city: billing?.address_city || undefined, state: billing?.address_state || undefined, postal_code: billing?.address_postal_code || undefined, country: billing?.address_country || undefined },
                },
            });
            if (sErr) { notify({ message: sErr.message || 'Card validation failed', severity: 'error' }); return; }
            if (!paymentMethod) { notify({ message: 'Failed to create payment method', severity: 'error' }); return; }
            await paymentService.addPaymentMethod(paymentMethod.id);
            cardNum.clear();
            elements.getElement(CardExpiryElement)?.clear();
            elements.getElement(CardCvcElement)?.clear();
            setCardholderName('');
            setCardDialogOpen(false);
            notify({ message: 'Card added successfully', severity: 'success', autoCloseDuration: 3000 });
            await loadData();
        } catch (err) {
            notify({ message: err instanceof Error ? err.message : 'Failed to add card', severity: 'error' });
        } finally {
            setIsAddingCard(false);
        }
    };

    const handleRemove = async () => {
        if (!removeTarget) return;
        setIsRemoving(true);
        try {
            await paymentService.removePaymentMethod(removeTarget.payment_method_id);
            setRemoveTarget(null);
            notify({ message: 'Card removed', severity: 'success', autoCloseDuration: 3000 });
            await loadData();
        } catch (err) { notify({ message: err instanceof Error ? err.message : 'Failed to remove', severity: 'error' }); }
        finally { setIsRemoving(false); }
    };

    const handleSetDefault = async (id: string) => {
        setSettingDefaultId(id);
        try { await paymentService.setDefaultPaymentMethod(id); notify({ message: 'Default updated', severity: 'success', autoCloseDuration: 3000 }); await loadData(); }
        catch (err) { notify({ message: err instanceof Error ? err.message : 'Failed', severity: 'error' }); }
        finally { setSettingDefaultId(null); }
    };

    return (
        <Box sx={{ maxWidth: 1200, width: '100%', mx: 'auto', px: { xs: 2, sm: 3 }, py: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Typography variant="h5" fontWeight={700}>Payment</Typography>
            </Box>

            {isLoading && (
                <Box data-testid="payment-methods-loading" sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress aria-label="Loading payment methods" />
                </Box>
            )}

            {error && !isLoading && (
                <Alert data-testid="payment-methods-error" severity="error"
                    action={<Button color="inherit" size="small" onClick={loadData}>Retry</Button>}>{error}</Alert>
            )}

            {!isLoading && !error && (
                <>
                    <Grid container spacing={3}>
                        {/* ── Billing Information ── */}
                        {!hideBilling && (
                            <Grid item xs={12} md={vertical ? 12 : 6}>
                                <Card sx={{ ...sectionCard, height: '100%' }}>
                                    <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                            <ReceiptLongIcon sx={{ color: 'primary.main' }} />
                                            <Typography variant="h6" fontWeight={600}>Billing Information</Typography>
                                            <Button size="small" startIcon={billing ? <EditIcon /> : <AddIcon />}
                                                onClick={openBillingDialog} sx={{ ml: 'auto', textTransform: 'none' }}>
                                                {billing?.name ? 'Edit' : 'Add Billing Info'}
                                            </Button>
                                        </Box>
                                        {billing?.name ? (
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                <Typography variant="body1" fontWeight={500}>{billing.name}</Typography>
                                                {billing.email && <Typography variant="body2" color="text.secondary">{billing.email}</Typography>}
                                                {billing.phone && <Typography variant="body2" color="text.secondary">{billing.phone}</Typography>}
                                                {billing.address_line1 && (
                                                    <Typography variant="body2" color="text.secondary">
                                                        {billing.address_line1}{billing.address_city ? `, ${billing.address_city}` : ''}
                                                        {billing.address_state ? `, ${billing.address_state}` : ''} {billing.address_postal_code}
                                                        {billing.address_country ? ` ${billing.address_country}` : ''}
                                                    </Typography>
                                                )}
                                            </Box>
                                        ) : (
                                            <Box sx={{ textAlign: 'center', py: 3 }}>
                                                <ReceiptLongIcon sx={{ fontSize: 40, opacity: 0.3, mb: 1 }} />
                                                <Typography variant="body2" color="text.secondary">No billing information saved</Typography>
                                            </Box>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                        )}

                        {/* ── Saved Cards ── */}
                        <Grid item xs={12} md={(hideBilling || vertical) ? 12 : 6}>
                            <Card sx={{ ...sectionCard, height: '100%' }}>
                                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                        <CreditCardIcon sx={{ color: 'primary.main' }} />
                                        <Typography variant="h6" fontWeight={600}>Wallet</Typography>
                                        <Button size="small" startIcon={<AddIcon />} onClick={() => setCardDialogOpen(true)}
                                            sx={{ ml: 'auto', textTransform: 'none' }}>Add New Card</Button>
                                    </Box>
                                    {methods.length === 0 ? (
                                        <Box data-testid="payment-methods-empty" sx={{ textAlign: 'center', py: 4 }}>
                                            <CreditCardIcon sx={{ fontSize: 40, opacity: 0.3, mb: 1 }} />
                                            <Typography variant="body2" color="text.secondary">No saved cards</Typography>
                                        </Box>
                                    ) : (
                                        <Grid container spacing={2} data-testid="payment-methods-grid">
                                            {methods.map((m) => (
                                                <Grid item xs={12} key={m.payment_method_id}>
                                                    <Card data-testid="payment-method-card" sx={{
                                                        ...sectionCard, borderColor: m.is_default ? 'primary.main' : undefined,
                                                        '&:hover': { borderColor: 'rgba(255,255,255,0.2)', transform: 'translateY(-2px)', boxShadow: '0 4px 12px rgba(0,0,0,0.3)' },
                                                        transition: 'border-color 0.2s, transform 0.2s, box-shadow 0.2s',
                                                    }}>
                                                        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5, '&:last-child': { pb: 1.5 } }}>
                                                            <CreditCardIcon fontSize="small" sx={{ color: 'primary.main' }} />
                                                            <Typography variant="subtitle2" fontWeight={600} sx={{ minWidth: 140 }}>
                                                                {formatBrand(m.card_brand)} •••• {m.last_four}
                                                            </Typography>
                                                            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>
                                                                {String(m.exp_month).padStart(2, '0')}/{m.exp_year}
                                                            </Typography>
                                                            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                {m.is_default ? (
                                                                    <Chip label="Default" size="small" color="primary" icon={<StarIcon />} data-testid="default-badge" />
                                                                ) : (
                                                                    <Button size="small" variant="outlined" sx={{ textTransform: 'none' }}
                                                                        onClick={() => handleSetDefault(m.payment_method_id)}
                                                                        disabled={settingDefaultId === m.payment_method_id}
                                                                        aria-label={`Set ${formatBrand(m.card_brand)} ending in ${m.last_four} as default`}>
                                                                        {settingDefaultId === m.payment_method_id ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                                                                        Set as default
                                                                    </Button>
                                                                )}
                                                                <IconButton size="small" color="error" onClick={() => setRemoveTarget(m)}
                                                                    aria-label={`Remove ${formatBrand(m.card_brand)} ending in ${m.last_four}`}>
                                                                    <DeleteIcon fontSize="small" />
                                                                </IconButton>
                                                            </Box>
                                                        </CardContent>
                                                    </Card>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </>
            )}

            {/* ── Billing Info Dialog ── */}
            <Dialog open={billingDialogOpen} onClose={() => !isSavingBilling && setBillingDialogOpen(false)} maxWidth="sm" fullWidth disableEnforceFocus>
                <DialogTitle>Billing Information</DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth size="small" label="Full Name" required value={bf.name}
                                onChange={(e) => setBf({ ...bf, name: e.target.value })}
                                error={!!billingErrors.name} helperText={billingErrors.name} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth size="small" label="Email" type="email" required value={bf.email}
                                onChange={(e) => setBf({ ...bf, email: e.target.value })}
                                error={!!billingErrors.email} helperText={billingErrors.email} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth size="small" label="Phone" type="tel" value={bf.phone}
                                onChange={(e) => setBf({ ...bf, phone: e.target.value })}
                                placeholder="+1 (555) 123-4567" />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small" required error={!!billingErrors.country}>
                                <InputLabel>Country</InputLabel>
                                <Select value={bf.country} label="Country"
                                    onChange={(e: SelectChangeEvent) => setBf({ ...bf, country: e.target.value })}>
                                    {COUNTRIES.map((c) => (
                                        <MenuItem key={c.code} value={c.code}>{c.name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth size="small" label="Street Address" value={bf.line1}
                                onChange={(e) => setBf({ ...bf, line1: e.target.value })} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField fullWidth size="small" label="City" value={bf.city}
                                onChange={(e) => setBf({ ...bf, city: e.target.value })} />
                        </Grid>
                        <Grid item xs={6} sm={4}>
                            <TextField fullWidth size="small" label="State" value={bf.state}
                                onChange={(e) => setBf({ ...bf, state: e.target.value })} />
                        </Grid>
                        <Grid item xs={6} sm={4}>
                            <TextField fullWidth size="small" label="ZIP Code" value={bf.zip}
                                onChange={(e) => setBf({ ...bf, zip: e.target.value })} />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setBillingDialogOpen(false)} disabled={isSavingBilling}>Cancel</Button>
                    <Button variant="contained" onClick={handleSaveBilling} disabled={isSavingBilling} sx={{ textTransform: 'none' }}>
                        {isSavingBilling ? <><CircularProgress size={18} sx={{ mr: 1, color: 'inherit' }} />Saving…</> : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Add Card Dialog ── */}
            <Dialog open={cardDialogOpen} onClose={() => !isAddingCard && setCardDialogOpen(false)} maxWidth="sm" fullWidth disableEnforceFocus>
                <DialogTitle>Add Payment Card</DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12}>
                            <TextField fullWidth size="small" label="Cardholder Name" required value={cardholderName}
                                onChange={(e) => { setCardholderName(e.target.value); setCardNameError(''); }}
                                placeholder="Name on card"
                                error={!!cardNameError} helperText={cardNameError} />
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>Card Number *</Typography>
                            <Box sx={stripeFieldBox}>
                                <CardNumberElement options={ELEMENT_OPTIONS}
                                    onChange={(e) => setCardFieldsComplete(prev => ({ ...prev, number: e.complete }))} />
                            </Box>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>Expiration *</Typography>
                            <Box sx={stripeFieldBox}>
                                <CardExpiryElement options={ELEMENT_OPTIONS}
                                    onChange={(e) => setCardFieldsComplete(prev => ({ ...prev, expiry: e.complete }))} />
                            </Box>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>CVC *</Typography>
                            <Box sx={stripeFieldBox}>
                                <CardCvcElement options={ELEMENT_OPTIONS}
                                    onChange={(e) => setCardFieldsComplete(prev => ({ ...prev, cvc: e.complete }))} />
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCardDialogOpen(false)} disabled={isAddingCard}>Cancel</Button>
                    <Button variant="contained" onClick={handleAddCard} disabled={isAddingCard || !stripe} sx={{ textTransform: 'none' }}>
                        {isAddingCard ? <><CircularProgress size={18} sx={{ mr: 1, color: 'inherit' }} />Saving…</> : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Remove Confirmation ── */}
            <Dialog open={!!removeTarget} onClose={() => !isRemoving && setRemoveTarget(null)} disableEnforceFocus>
                <DialogTitle>Remove Card</DialogTitle>
                <DialogContent>
                    <Typography>Remove {removeTarget ? `${formatBrand(removeTarget.card_brand)} ending in ${removeTarget.last_four}` : 'this card'}? This cannot be undone.</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRemoveTarget(null)} disabled={isRemoving}>Cancel</Button>
                    <Button color="error" onClick={handleRemove} disabled={isRemoving}>
                        {isRemoving ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}Remove
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
